import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type FoldTone = 0 | 1 | 2 | 3 | 4;

export const toneClass: Record<FoldTone, string> = {
  0: "bg-[var(--surface-0)] text-[var(--foreground)]",
  1: "bg-[var(--surface-1)] text-[var(--foreground)]",
  2: "bg-[var(--surface-2)] text-[var(--foreground)]",
  3: "bg-[var(--surface-3)] text-[var(--foreground)]",
  4: "bg-[var(--surface-4)] text-[var(--on-dark-fold)]",
};

interface SectionProps {
  children: ReactNode;
  tone?: FoldTone;
  id?: string;
  className?: string;
  containerClassName?: string;
}

/** A page fold. Each fold declares its tone so the header can adopt the colour
 *  of whatever section sits beneath it while scrolling. */
export function Section({
  children,
  tone = 0,
  id,
  className,
  containerClassName,
}: SectionProps) {
  return (
    <section
      id={id}
      data-fold-tone={tone}
      className={cn("relative w-full py-14 sm:py-20 lg:py-24", toneClass[tone], className)}
    >
      <div className={cn("mx-auto w-full max-w-6xl px-5 sm:px-8", containerClassName)}>
        {children}
      </div>
    </section>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  align = "left",
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  align?: "left" | "center";
}) {
  return (
    <div className={cn("max-w-2xl", align === "center" && "mx-auto text-center")}>
      {eyebrow ? (
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] opacity-70">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="font-heading text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
        {title}
      </h2>
      {subtitle ? <p className="mt-4 text-base opacity-75 sm:text-lg">{subtitle}</p> : null}
    </div>
  );
}
